import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook personnalisé pour auto-refresh intelligent des données
 * Optimisé pour éviter les appels inutiles et économiser les ressources
 * Version améliorée pour la collaboration multi-utilisateurs
 */
function useAutoRefresh(
  fetchFunction,
  dependencies = [],
  options = {}
) {
  const {
    interval = 15000, // 15 secondes par défaut
    enabled = true,
    onError = null,
    maxRetries = 3,
    backoffMultiplier = 2,
    collaborationMode = false // Mode collaboration pour refresh plus fréquent
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  
  const intervalRef = useRef(null);
  const isVisibleRef = useRef(true);
  const lastDataRef = useRef(null);

  // Détection de visibilité de l'onglet
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
      
      // Si l'onglet redevient visible, refresh immédiat
      if (isVisibleRef.current && enabled) {
        performRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  // Fonction de refresh avec gestion d'erreurs
  const performRefresh = useCallback(async () => {
    if (!enabled || isRefreshing) return;

    try {
      setIsRefreshing(true);
      const newData = await fetchFunction();
      
      // Vérifier si les données ont changé
      const dataChanged = JSON.stringify(newData) !== JSON.stringify(lastDataRef.current);
      lastDataRef.current = newData;
      
      setLastRefresh(new Date());
      setRetryCount(0); // Reset retry count on success
      
      return { success: true, dataChanged, data: newData };
    } catch (error) {
      console.warn('Auto-refresh error:', error);
      
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      
      if (onError) {
        onError(error, newRetryCount);
      }
      
      return { success: false, error, retryCount: newRetryCount };
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchFunction, enabled, isRefreshing, retryCount, onError]);

  // Configuration de l'intervalle avec backoff exponentiel
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Calcul de l'intervalle avec backoff en cas d'erreurs répétées
    // En mode collaboration, refresh plus fréquent
    const baseInterval = collaborationMode ? Math.min(interval, 5000) : interval;
    const currentInterval = retryCount > 0 
      ? Math.min(baseInterval * Math.pow(backoffMultiplier, retryCount), 120000) // Max 2 minutes
      : baseInterval;

    intervalRef.current = setInterval(() => {
      // Ne refresh que si l'onglet est visible
      if (isVisibleRef.current) {
        performRefresh();
      }
    }, currentInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, retryCount, performRefresh, backoffMultiplier]);

  // Refresh initial et lors de changements de dépendances
  useEffect(() => {
    if (enabled) {
      performRefresh();
    }
  }, [enabled, ...dependencies]);

  // Fonction pour forcer un refresh manuel
  const forceRefresh = useCallback(() => {
    return performRefresh();
  }, [performRefresh]);

  // Fonction pour mettre en pause/reprendre
  const toggleEnabled = useCallback((newEnabled) => {
    if (newEnabled !== undefined) {
      return newEnabled;
    }
    return !enabled;
  }, [enabled]);

  return {
    isRefreshing,
    lastRefresh,
    retryCount,
    forceRefresh,
    toggleEnabled,
    isEnabled: enabled
  };
}

export default useAutoRefresh;
